import React, { useState, useEffect } from "react"

export default function SuperSecretPhone() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedGames, setExpandedGames] = useState({})
  const [selectedSuggestions, setSelectedSuggestions] = useState({})

  useEffect(() => {
    fetchData()
    // Refresh every 10 seconds
    const interval = setInterval(fetchData, 10000)
    return () => clearInterval(interval)
  }, [])

  const fetchData = async () => {
    try {
      const response = await fetch("/api/super_secret/phone_data")
      if (response.ok) {
        const result = await response.json()
        setData(result)
        setError(null)
      } else {
        setError("No active show")
      }
    } catch (err) {
      console.error("Error fetching data:", err)
      setError("Failed to load data")
    } finally {
      setLoading(false)
    }
  }

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value
    try {
      const response = await fetch("/api/super_secret/set_status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus })
      })
      if (response.ok) {
        setData(prev => ({ ...prev, status: newStatus }))
      }
    } catch (err) {
      console.error("Error updating status:", err)
    }
  }

  const handleGameChange = async (e) => {
    const gameId = e.target.value
    try {
      const response = await fetch("/api/super_secret/set_current_game", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ game_id: gameId || null })
      })
      if (response.ok) {
        setData(prev => ({ ...prev, current_game_id: gameId ? parseInt(gameId) : null }))
      }
    } catch (err) {
      console.error("Error updating current game:", err)
    }
  }

  const toggleGameExpansion = (gameId) => {
    setExpandedGames(prev => ({
      ...prev,
      [gameId]: !prev[gameId]
    }))
  }

  const toggleSuggestionSelection = (gameId, suggestionId) => {
    setSelectedSuggestions(prev => {
      const gameSelections = prev[gameId] || []
      if (gameSelections.includes(suggestionId)) {
        return {
          ...prev,
          [gameId]: gameSelections.filter(id => id !== suggestionId)
        }
      } else {
        return {
          ...prev,
          [gameId]: [...gameSelections, suggestionId]
        }
      }
    })
  }

  const hideSuggestions = async (gameId) => {
    const ids = selectedSuggestions[gameId] || []
    if (ids.length === 0) return

    try {
      const response = await fetch("/api/admin/parsed_responses/hide_multiple", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      })

      if (response.ok) {
        setSelectedSuggestions(prev => ({ ...prev, [gameId]: [] }))
        fetchData()
      }
    } catch (err) {
      console.error("Error hiding suggestions:", err)
    }
  }

  const unhideSuggestion = async (suggestionId) => {
    try {
      const response = await fetch(`/api/admin/parsed_responses/${suggestionId}/unhide`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      })

      if (response.ok) {
        fetchData()
      }
    } catch (err) {
      console.error("Error unhiding suggestion:", err)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-xl text-gray-600">Loading...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-xl text-red-600">{error}</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">
          {data.show_title}
        </h1>

        {/* Controls Section */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6 space-y-4">
          {/* Status Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Show Status
            </label>
            <select
              value={data.status}
              onChange={handleStatusChange}
              className="w-full bg-gray-50 border border-gray-300 text-gray-800 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {data.statuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          {/* Current Game Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Current Game
            </label>
            <select
              value={data.current_game_id || ""}
              onChange={handleGameChange}
              className="w-full bg-gray-50 border border-gray-300 text-gray-800 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">None selected</option>
              {data.games.map((game, index) => (
                <option key={game.game_id} value={game.game_id}>
                  {index + 1}. {game.game_type}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Ask-Fors Section */}
        <p className="text-sm text-gray-500 mb-3">Processed Suggestions</p>
        <div className="space-y-3">
          {data.games.filter(g => g.ask_for_id).length === 0 ? (
            <p className="text-gray-500 text-center py-8">No ask-fors found</p>
          ) : (
            data.games.filter(g => g.ask_for_id).map((game, index) => (
              <div
                key={game.ask_for_id}
                className="bg-white rounded-lg shadow-sm p-4 border border-gray-200"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h2 className="font-semibold text-gray-800">
                      {game.game_type}
                    </h2>
                    {game.ask_for_prompt && (
                      <p className="text-sm text-gray-500 mt-1">
                        {game.ask_for_prompt}
                      </p>
                    )}
                  </div>
                  <div className="text-right ml-4">
                    <div className="text-2xl font-bold text-blue-600">
                      {game.processed_count}
                    </div>
                    <div className="text-xs text-gray-500">
                      processed
                    </div>
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-400">
                  {game.total_suggestions} raw suggestion{game.total_suggestions !== 1 ? 's' : ''}
                </div>

                {/* Expandable suggestions section */}
                {game.parsed_responses && game.parsed_responses.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => toggleGameExpansion(game.game_id)}
                      className="flex items-center gap-2 text-sm font-medium text-gray-600"
                    >
                      <span className={`transform transition-transform ${expandedGames[game.game_id] ? 'rotate-90' : ''}`}>
                        ▶
                      </span>
                      {game.parsed_responses.filter(s => !s.hidden).length} visible, {game.parsed_responses.filter(s => s.hidden).length} hidden
                    </button>

                    {expandedGames[game.game_id] && (
                      <div className="mt-3 bg-gray-50 rounded-lg p-3">
                        {/* Action button */}
                        {(selectedSuggestions[game.game_id]?.length > 0) && (
                          <div className="mb-3">
                            <button
                              onClick={() => hideSuggestions(game.game_id)}
                              className="px-3 py-2 text-sm bg-red-500 hover:bg-red-600 text-white rounded font-semibold"
                            >
                              Don't Use ({selectedSuggestions[game.game_id].length})
                            </button>
                          </div>
                        )}

                        {/* Visible suggestions */}
                        <div className="space-y-2">
                          {game.parsed_responses.filter(s => !s.hidden).length === 0 ? (
                            <p className="text-sm text-gray-400 italic">No visible suggestions</p>
                          ) : (
                            game.parsed_responses.filter(s => !s.hidden).map(suggestion => (
                              <label
                                key={suggestion.id}
                                className={`flex items-center gap-3 p-2 rounded cursor-pointer ${
                                  selectedSuggestions[game.game_id]?.includes(suggestion.id) ? 'bg-blue-50 border border-blue-200' : 'bg-white'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedSuggestions[game.game_id]?.includes(suggestion.id) || false}
                                  onChange={() => toggleSuggestionSelection(game.game_id, suggestion.id)}
                                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-800">
                                  {suggestion.content}
                                </span>
                              </label>
                            ))
                          )}
                        </div>

                        {/* Hidden suggestions */}
                        {game.parsed_responses.filter(s => s.hidden).length > 0 && (
                          <div className="mt-4 pt-3 border-t border-gray-200">
                            <p className="text-xs font-semibold text-gray-500 mb-2">Hidden:</p>
                            <div className="space-y-2">
                              {game.parsed_responses.filter(s => s.hidden).map(suggestion => (
                                <div
                                  key={suggestion.id}
                                  className="flex items-center justify-between p-2 rounded bg-red-50"
                                >
                                  <span className="text-sm text-gray-500 line-through">
                                    {suggestion.content}
                                  </span>
                                  <button
                                    onClick={() => unhideSuggestion(suggestion.id)}
                                    className="px-2 py-1 text-xs bg-green-500 hover:bg-green-600 text-white rounded"
                                  >
                                    Restore
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <p className="text-xs text-gray-400 text-center mt-6">
          Auto-refreshes every 10 seconds
        </p>
      </div>
    </div>
  )
}
